import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@ui/index';
import { AuthService } from '@/shared/api/services/authService';
import type { User, ValidateTokenResponse } from '@/shared/api/types';
import { emitGlobalAlert } from '@/shared/lib/alertEvents';
import { useAuth } from '@/shared/state/store';

const DEFAULT_COUNTDOWN_SECONDS = 60;
const RECENT_ACTIVITY_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

const isValidateTokenResponse = (value: unknown): value is ValidateTokenResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.code === 'string';
};

const hasDataField = (value: unknown): value is { data?: unknown } =>
  typeof value === 'object' && value !== null && 'data' in value;

const extractValidatePayload = (value: unknown): ValidateTokenResponse | undefined => {
  if (isValidateTokenResponse(value)) {
    return value;
  }
  if (hasDataField(value) && isValidateTokenResponse(value.data)) {
    return value.data;
  }
  return undefined;
};

const SessionExpiryManager: React.FC = () => {
  const { isAuthenticated, logout, setToken, setUser, user } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState<string>('Your session will expire soon.');
  const [countdown, setCountdown] = useState<number>(DEFAULT_COUNTDOWN_SECONDS);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [keepAliveInProgress, setKeepAliveInProgress] = useState(false);

  const countdownTimerRef = useRef<number | null>(null);
  const validatedRef = useRef(false);
  const validationInFlightRef = useRef(false);
  const lastActivityAtRef = useRef<number>(Date.now());

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const markUserActivity = useCallback(() => {
    const now = Date.now();
    // Avoid excessive writes from high-frequency events like mousemove.
    if (now - lastActivityAtRef.current < 1000) {
      return;
    }
    lastActivityAtRef.current = now;
  }, []);

  const isUserRecentlyActive = useCallback((): boolean => {
    return Date.now() - lastActivityAtRef.current <= RECENT_ACTIVITY_WINDOW_MS;
  }, []);

  const startCountdown = useCallback(
    (seconds?: number) => {
      clearCountdown();
      const initial = typeof seconds === 'number' && seconds > 0 ? seconds : DEFAULT_COUNTDOWN_SECONDS;
      setCountdown(initial);
      countdownTimerRef.current = window.setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    },
    [clearCountdown]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    // Track "recent activity" so we can silently refresh while the user is actively using the app.
    const handler = () => markUserActivity();
    const opts: AddEventListenerOptions = { passive: true };

    window.addEventListener('pointerdown', handler, opts);
    window.addEventListener('mousedown', handler, opts);
    window.addEventListener('keydown', handler, opts);
    window.addEventListener('scroll', handler, opts);
    window.addEventListener('mousemove', handler, opts);
    window.addEventListener('touchstart', handler, opts);
    document.addEventListener('visibilitychange', handler, opts);

    return () => {
      window.removeEventListener('pointerdown', handler, opts);
      window.removeEventListener('mousedown', handler, opts);
      window.removeEventListener('keydown', handler, opts);
      window.removeEventListener('scroll', handler, opts);
      window.removeEventListener('mousemove', handler, opts);
      window.removeEventListener('touchstart', handler, opts);
      document.removeEventListener('visibilitychange', handler, opts);
    };
  }, [isAuthenticated, markUserActivity]);

  useEffect(() => {
    if (!showModal) return;
    if (countdown === 0) {
      void logout();
    }
  }, [countdown, logout, showModal]);

  useEffect(() => {
    return () => {
      clearCountdown();
    };
  }, [clearCountdown]);

  const handleExpired = useCallback(
    async (message?: string) => {
      emitGlobalAlert({
        severity: 'error',
        message: message || 'Session expired or invalid',
        autoHide: true,
        duration: 4000,
      });
      await logout();
    },
    [logout]
  );

  const handleKeepAlive = useCallback(async (options?: { silent?: boolean }) => {
    if (keepAliveInProgress) return;

    setKeepAliveInProgress(true);
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('Missing refresh token');
      }

      const refreshed = await AuthService.refreshToken(
        { refresh_token: refreshToken },
        { skipErrorCapture: true }
      );

      if (refreshed?.access_token) {
        setToken(refreshed.access_token);
        localStorage.setItem('authToken', refreshed.access_token);
      }
      if (refreshed?.refresh_token) {
        localStorage.setItem('refreshToken', refreshed.refresh_token);
      }

      if (refreshed?.emailAddress || refreshed?.displayName || user) {
        const nextUser: User = {
          emailAddress: refreshed.emailAddress ?? user?.emailAddress ?? '',
          displayName: refreshed.displayName ?? user?.displayName ?? '',
          userId: refreshed.userId ?? user?.userId ?? 0,
          userName: refreshed.userName ?? user?.userName ?? '',
          forceChangePassword: refreshed.forceChangePassword ?? user?.forceChangePassword ?? false,
          twoStepVerificationRequired:
            refreshed.twoStepVerificationRequired ?? user?.twoStepVerificationRequired ?? false,
        };
        if (user?.onboarding) {
          nextUser.onboarding = user.onboarding;
        }
        setUser(nextUser);
      }

      if (options?.silent !== true) {
        emitGlobalAlert({
          severity: 'success',
          message: 'Session extended.',
          autoHide: true,
          duration: 3000,
        });
      }
      setShowModal(false);
      clearCountdown();
    } catch (error) {
      if (options?.silent !== true) {
        emitGlobalAlert({
          severity: 'error',
          message: 'Unable to validate session. Please sign in again.',
          autoHide: true,
          duration: 4000,
        });
      }
      await logout();
    } finally {
      setKeepAliveInProgress(false);
    }
  }, [clearCountdown, keepAliveInProgress, logout, setToken, setUser, user]);

  const maybeAutoKeepAlive = useCallback(
    async (): Promise<boolean> => {
      // Only auto-refresh when the user is actively using the app.
      // If the user is idle (or the tab is hidden), keep the existing confirmation dialog.
      const isVisible =
        typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
      if (!isVisible) return false;
      if (!isUserRecentlyActive()) return false;
      if (keepAliveInProgress) return true; // already in progress; treat as handled

      setShowModal(false);
      clearCountdown();
      await handleKeepAlive({ silent: true });
      return true;
    },
    [clearCountdown, handleKeepAlive, isUserRecentlyActive, keepAliveInProgress]
  );

  // Decide whether to show the dialog or silently refresh the session.
  const handleValidateResponseWithAutoRefresh = useCallback(
    async (result: ValidateTokenResponse | undefined) => {
      if (!result) {
        void handleExpired('Unable to validate session.');
        return;
      }

      const { code, message, expiresInSeconds } = result;
      switch (code) {
        case 'TOKEN_VALID':
          setShowModal(false);
          clearCountdown();
          break;
        case 'TOKEN_EXPIRING_SOON': {
          // If the user is active, extend the session without interrupting them.
          try {
            const handled = await maybeAutoKeepAlive();
            if (handled) {
              return;
            }
          } catch {
            // Fall back to showing the dialog if silent refresh fails for any reason.
          }

          setModalMessage('Your session will expire soon.');
          setShowModal(true);
          startCountdown(expiresInSeconds);
          break;
        }
        default:
          void handleExpired('Session expired or invalid');
      }
    },
    [clearCountdown, handleExpired, maybeAutoKeepAlive, startCountdown]
  );

  const runValidation = useCallback(
    async (useValidatedGuard = false) => {
      if (!isAuthenticated) {
        return;
      }
      if (useValidatedGuard && validatedRef.current) {
        return;
      }
      if (validationInFlightRef.current) {
        return;
      }

      validationInFlightRef.current = true;
      setValidationInProgress(true);
      try {
        const result = await AuthService.validateToken();
        if (useValidatedGuard) {
          validatedRef.current = true;
        }
        const payload = extractValidatePayload(result);
        await handleValidateResponseWithAutoRefresh(payload);
      } catch (error) {
        if (useValidatedGuard) {
          validatedRef.current = true;
        }
        void handleExpired('Unable to validate session. Please sign in again.');
      } finally {
        validationInFlightRef.current = false;
        setValidationInProgress(false);
      }
    },
    [handleExpired, handleValidateResponseWithAutoRefresh, isAuthenticated]
  );

  useEffect(() => {
    if (isAuthenticated) {
      void runValidation(true);
    } else {
      setShowModal(false);
      clearCountdown();
      validatedRef.current = false;
      validationInFlightRef.current = false;
    }
  }, [clearCountdown, isAuthenticated, runValidation]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void runValidation(false);
    }, 180000); // every 3 minutes

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, runValidation]);

  const progressPercent = useMemo(() => {
    const capped = Math.max(0, Math.min(DEFAULT_COUNTDOWN_SECONDS, countdown));
    return (capped / DEFAULT_COUNTDOWN_SECONDS) * 100;
  }, [countdown]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog data-testid="app-session-expiry-manager-modal-action" open={showModal} maxWidth="xs" fullWidth>
      <DialogTitle data-testid="app-session-expiry-manager-header-session-expiring-soon">Session expiring soon</DialogTitle>
      <DialogContent>
        <Typography data-testid="app-session-expiry-manager-text-action" variant="body1" sx={{ mb: 2 }}>
          {modalMessage}
        </Typography>
        <Box data-testid="app-session-expiry-manager-container-action" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography data-testid="app-session-expiry-manager-text-logging-out-ins" variant="body2" color="text.secondary">
            Logging out in {countdown}s
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progressPercent} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button data-testid="app-session-expiry-manager-btn-logout" onClick={logout} color="error" variant="outlined" disabled={validationInProgress}>
          Logout now
        </Button>
        <Button data-testid="app-session-expiry-manager-btn-handle-keep-alive"
          onClick={handleKeepAlive}
          variant="contained"
          disabled={keepAliveInProgress}
        >
          {keepAliveInProgress ? 'Refreshing…' : 'Keep me signed in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiryManager;
